import React, { forwardRef } from "react";
import { cn } from "../../lib/utils.js";
import { Button } from "../../atoms/Button/index.js";
import { Chip } from "../../atoms/Chip/index.js";

/* ---------------- FilterBar / FilterChip / FilterPanel (C8 #2) ----------------
   The filter pattern (strategy §17.3). Data-blind and composable: the design
   system owns the chip rail + panel chrome; filter *values*, URL state, and
   saved filter sets are the consuming app's concern.

   - FilterChip  — a removable applied-filter chip (label + optional value).
   - FilterBar   — a wrapping rail of chips + a "Clear all" action + a trailing
                   slot (e.g. an "Add filter" button or the FilterPanel trigger).
   - FilterPanel — a labeled container for filter controls with a Clear/Apply
                   footer, or a bordered toolbar row (FilterPanel.tsx). */

export interface FilterChipProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  "onSelect"
> {
  label: React.ReactNode;
  /** Optional selected value shown after the label (e.g. "Owner: Aria"). */
  value?: React.ReactNode;
  /** When provided, renders a remove (×) button. */
  onRemove?: (() => void) | undefined;
  /** Accessible name for the remove button (default "Remove filter"). */
  removeLabel?: string;
}

/* FilterChip is Chip's static, removable form (atoms/Chip): the same frame
   and the same remove control (ChipRemoveButton), with its own default
   removeLabel. */
export const FilterChip = forwardRef<HTMLSpanElement, FilterChipProps>(
  function FilterChip({ removeLabel = "Remove filter", ...rest }, ref) {
    return <Chip ref={ref} removeLabel={removeLabel} {...rest} />;
  },
);

export interface ActiveFilter {
  id: string;
  label: React.ReactNode;
  value?: React.ReactNode;
}

export interface FilterBarProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Applied filters, rendered as removable chips. */
  filters: ReadonlyArray<ActiveFilter>;
  /** Remove a single filter by id. */
  onRemove?: (id: string) => void;
  /** Clear every filter. The "Clear all" action shows only when ≥1 filter. */
  onClearAll?: () => void;
  /** Label for the clear-all action (default "Clear all"). */
  clearAllLabel?: string;
  /** Trailing controls — e.g. an "Add filter" button or the FilterPanel trigger. */
  children?: React.ReactNode;
}

export const FilterBar = forwardRef<HTMLDivElement, FilterBarProps>(
  function FilterBar(
    {
      filters,
      onRemove,
      onClearAll,
      clearAllLabel = "Clear all",
      children,
      className,
      ...rest
    },
    ref,
  ) {
    return (
      <div
        ref={ref}
        role="group"
        aria-label="Filters"
        className={cn("flex flex-wrap items-center gap-2", className)}
        {...rest}
      >
        {filters.map((f) => (
          <FilterChip
            key={f.id}
            label={f.label}
            value={f.value}
            removeLabel={`Remove ${typeof f.label === "string" ? f.label : "filter"}`}
            onRemove={onRemove ? () => onRemove(f.id) : undefined}
          />
        ))}
        {onClearAll && filters.length > 0 && (
          <Button variant="ghost" size="sm" onClick={onClearAll}>
            {clearAllLabel}
          </Button>
        )}
        {children}
      </div>
    );
  },
);
